import { serialize } from "object-to-formdata";

import { bffRequest } from "../../shared/api/bff-request";
import { JSON_TO_FORM_DATA_OPTIONS } from "../../shared/constants/files";
import {
  ListResponse,
  PaginationRequestParams,
  SortingRequestParams,
} from "../../shared/types/api-types";

import {
  ManagingCompany,
  Order,
  OrderListItem,
  OrderSchemasDto,
  OrderSortColumn,
  TemplateType,
} from "./types";

export const getOrder = (id: string) =>
  bffRequest.get<Order>(`/orders/item?id=${id}`);

export interface GetOrdersFilter {
  number?: string;
  typeIds?: number[];
  subtypeIds?: number[];
  statuses?: string[];
  initiatorId?: string;
  executorId?: string;
  rentAreaIds?: string[];
  projectIds?: string[];
  personClientIds?: string[];
  executorIds?: string[];
  plannedDateFilter?: {
    start?: string;
    end?: string;
  };
  createdDateFilter?: {
    start?: string;
    end?: string;
  };
  isCompleted?: boolean;
}

interface GetOrdersRequest extends GetOrdersFilter {
  pagination: PaginationRequestParams;
  sorting?: SortingRequestParams<OrderSortColumn>;
}

export const getOrders = (body: GetOrdersRequest) =>
  bffRequest.post<ListResponse<OrderListItem>>("/orders/list", body);

export interface GetOrderTypesParams {
  projectIds?: string[];
}

export interface OrderTypeOptionDto {
  id: number;
  name: string;
  code: string;
}

export const getOrderTypes = (params: GetOrderTypesParams = {}) => {
  const query = params.projectIds?.length
    ? `?${params.projectIds.map((projectId) => `projectIds=${encodeURIComponent(projectId)}`).join("&")}`
    : "";

  return bffRequest.get<OrderTypeOptionDto[]>(`/orders/types${query}`);
};

export interface AddOrderFilesRequest {
  OrderId: string;
  FieldId: string;
  Files: File[];
}

export const addOrderFiles = (data: AddOrderFilesRequest) =>
  bffRequest.post("/orders/files", data, {
    headers: { "Content-Type": "multipart/form-data" },
  });

export interface GetDeleteOrderFileParams {
  orderId: string;
  fieldId: string;
  fileName: string;
}

export interface GetOrderTemplateImageParams {
  subtypeViewId: string;
  fileName: string;
}

const toBlobOrNull = (file: Blob | string | null | undefined) => {
  if (!(file instanceof Blob) || file.size === 0) {
    return null;
  }

  return file;
};

export const getOrderFile = ({
  orderId,
  fieldId,
  fileName,
}: GetDeleteOrderFileParams) =>
  bffRequest
    .get<Blob>(
      `/orders/file?orderId=${orderId}&fieldId=${fieldId}&fileName=${fileName}`,
      { responseType: "blob" },
    )
    .then((blob) => URL.createObjectURL(blob));

export const deleteOrderFile = ({
  orderId,
  fieldId,
  fileName,
}: GetDeleteOrderFileParams) =>
  bffRequest.delete(
    `/orders/file?orderId=${orderId}&fieldId=${fieldId}&fileName=${fileName}`,
  );

export const getOrderTemplateImage = ({
  subtypeViewId,
  fileName,
}: GetOrderTemplateImageParams) =>
  bffRequest
    .get<Blob | string>(
      `/orders/template-image?subtypeViewId=${encodeURIComponent(subtypeViewId)}&imageFileName=${encodeURIComponent(fileName)}`,
      { responseType: "blob" },
    )
    .then(toBlobOrNull)
    .catch(() => null);

export interface ChangeOrderStatusRequest {
  orderId: string;
  status: string;
}

interface ChangeOrderStatusResponse {
  otpConfirmationRequired: boolean;
}

export const changeOrderStatus = (body: ChangeOrderStatusRequest) =>
  bffRequest
    .post<ChangeOrderStatusResponse>("/orders/status", body)
    .then((response) => ({
      ...response,
      ...body,
    }));

export interface ConfirmChangeOrderStatusRequest {
  orderId: string;
  status: string;
  otpCode: string;
}

export interface OrderFieldsDto {
  fieldId: number;
  values: string[];
}

export interface OrderFilesDto {
  fieldId: number;
  files: File[];
}

export const confirmChangeOrderStatus = (
  body: ConfirmChangeOrderStatusRequest,
) => bffRequest.post("/orders/status/confirm", body);

export interface ChangeOrderFieldsRequest {
  orderId: string;
  fields: OrderFieldsDto[];
  fileFields: OrderFilesDto[];
}

export const changeOrderFields = (body: ChangeOrderFieldsRequest) => {
  const formData = serialize(body, JSON_TO_FORM_DATA_OPTIONS);
  return bffRequest.post("/orders/fields", formData);
};

export interface GetOrderTemplateViewsParams {
  projectIds?: string[];
  type?: string;
}

export const getOrderTemplateViews = (body: GetOrderTemplateViewsParams) =>
  bffRequest.post<{ types: TemplateType[] }>("/orders/template-views", body);

export const getCreateOrderSchema = (orderSubtypeId: number) =>
  bffRequest.get<OrderSchemasDto>(
    `/orders/create-schema?orderSubtypeId=${orderSubtypeId}`,
  );

export interface CreateOrderRequest {
  subtypeViewId: string;
  rentAreaId: string;
  fields: OrderFieldsDto[];
  fileFields: OrderFilesDto[];
}

export interface CreateOrderResponse {
  id: string;
}

export const createOrder = (body: CreateOrderRequest) => {
  const formData = serialize(body, JSON_TO_FORM_DATA_OPTIONS);
  return bffRequest.post<CreateOrderResponse>("/orders/create", formData);
};

export const getManagingCompany = () =>
  bffRequest.get<ManagingCompany>("/managing-company");
